import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'userIdInput', 'orderInput', 'orderDiv', 'userNameField', 'userEmailField', 'userImageField', 'signerTypeSelect',
    'userDetails', 'userDetailIcons', 'userImage', 'signerSelectContainer', 'signerSelect', 'signerTypeSelectContainer',
    'userPhoneInput', 'userCvrInput', 'externalFullNameInput', 'externalEmailInput', 'dragIcon',
    'buttonsGroup', 'setButton', 'container', 'signerDescriptionField', 'recruiterWorkflowInput', 'recruiterWorkflowSelect', 'recruiterWorkflowHidden'
  ];

  static values = {
    data: Array,
    workflowData: Array,
    defaultPhotoPath: String,
    internalSignerText: String,
    externalSignerText: String
  }

  static classes = ['hidden'];

  connect () {
    const selectedWorkflowId = parseInt(this.hasRecruiterWorkflowHiddenTarget ? this.recruiterWorkflowHiddenTarget.value : this.recruiterWorkflowSelectTarget.value);
    this.workflow = this.workflowDataValue.find((workflowData) => workflowData.id === selectedWorkflowId)
      || this.workflowDataValue[0];
    this.fullSignersData = this.dataValue.map(o => JSON.parse(o));
    const userId = this.signerSelectTarget.value;
    this.selectedSigner = null;
    if (userId && userId.trim() !== '') {
      this.selectedSigner = this.fullSignersData.find(e => e.id === parseInt(userId, 10));
    }
    if (this.element.classList.contains('editable')) {
      this.orderDivTarget.classList.add(this.hiddenClass);
    } else {
      this.orderDivTarget.classList.remove(this.hiddenClass);
    }
    this.recountOrder();
  }

  workflowDataValueChanged () {
    const selectedWorkflowId = parseInt(this.recruiterWorkflowSelectTarget.value);
    this.workflow = this.workflowDataValue.find((workflowData) => workflowData.id === selectedWorkflowId)
      || this.workflowDataValue[0];
    // Sync hidden field to valid workflow
    if (this.hasRecruiterWorkflowHiddenTarget && this.workflow) {
      this.recruiterWorkflowHiddenTarget.value = this.workflow.id;
    }
  }

  // ACTIONS

  signerTypeSelected(e) {
    this.setButtonTarget.setAttribute('disabled', 'disabled');
    const signerContainer = this.containerTarget.querySelector('.signer-box');
    const signerType = e.currentTarget.value;
    if (signerType === 'external') {
      signerContainer.classList.add('green-box');
      this.showExternalInputs();
    } else if (signerType === 'internal') {
      signerContainer.classList.remove('green-box');
      this.showSignerSelect();
    }
  }

  signerSelected(e) {
    const userId = e.currentTarget.value;
    if (userId && userId.trim() !== '') {
      this.selectedSigner = this.fullSignersData.find(e => e.id === parseInt(userId, 10));
      if (this.workflow && this.workflow.phone_presence && this.selectedSigner && (!this.selectedSigner.phone || this.selectedSigner.phone === '')) {
        this.userPhoneInputTarget.classList.remove(this.hiddenClass);
        this.userPhoneInputTarget.style.paddingRight = '33px';
        this.userPhoneInputTarget.style.paddingLeft = '0';
      } else {
        this.userPhoneInputTarget.classList.add(this.hiddenClass);
      }
    }
    this._updateSetButton();
  }

  // PRIVATE

  showExternalInputs() {
    this.signerSelectContainerTarget.classList.add(this.hiddenClass);
    this.externalFullNameInputTarget.classList.remove(this.hiddenClass);
    this.externalEmailInputTarget.classList.remove(this.hiddenClass);
    this.recruiterWorkflowInputTarget.classList.remove(this.hiddenClass);
    this.recruiterWorkflowInputTarget.style.paddingLeft = '33px';
    this.recruiterWorkflowInputTarget.style.paddingRight = '0';
    this.changeWorkflowSelect();
  }

  showSignerSelect() {
    this.externalFullNameInputTarget.classList.add(this.hiddenClass);
    this.externalEmailInputTarget.classList.add(this.hiddenClass);
    if (this.hasUserPhoneInputTarget) {
      this.userPhoneInputTarget.classList.add(this.hiddenClass);
    }
    if (this.hasUserCvrInputTarget) {
      this.userCvrInputTarget.classList.add(this.hiddenClass);
    }
    this.signerSelectContainerTarget.classList.remove(this.hiddenClass);
    this.recruiterWorkflowInputTarget.classList.remove(this.hiddenClass);
    this.recruiterWorkflowInputTarget.style.paddingLeft = '0';
    this.recruiterWorkflowInputTarget.style.paddingRight = '33px';
    this.filterAlreadySelectedSigners();
  }

  filterAlreadySelectedSigners() {
    const usedIds = new Set();
    document.querySelectorAll('.contract-signers input[data-signers-target="userIdInput"]').forEach(input => {
      if (input.value && input.value.trim() !== '') {
        const container = input.closest('.signer-container');
        const destroyInput = container && container.querySelector('input[data-nested-form-target="destroyInput"], input[data-hr-nested-form-target="destroyInput"]');
        if (!destroyInput || destroyInput.value !== '1') {
          usedIds.add(input.value);
        }
      }
    });
    const select = this.signerSelectContainerTarget.querySelector('md-outlined-select');
    if (!select) return;
    select.querySelectorAll('md-select-option').forEach(option => {
      if (option.value && option.value.trim() !== '') {
        option.hidden = usedIds.has(option.value);
        if (option.hidden && option.value === select.value) {
          select.value = '';
        }
      }
    });
  }

  setUserId(userId) {
    this.userIdInputTarget.value = userId;
  }

  updateFullDataAndShowSigner(userId) {
    const fullData = this.dataValue.map(o => JSON.parse(o));
    const selectedUserData = fullData.find(e => e.id === parseInt(userId, 10));
    this.showSigner(selectedUserData);
  }

  showSigner(data) {
    this.fillSignerData(data);
    this.swapSelectToForm();
  }

  fillSignerData(data) {
    this.userNameFieldTarget.innerHTML = data.name;
    this.userEmailFieldTarget.innerHTML = data.email;
    const signerType = this.signerTypeSelectTarget.value;
    const signingFlowDesc = this._signingFlowDescription();
    if (signerType === 'external') {
      this.signerDescriptionFieldTarget.innerHTML = this.externalSignerTextValue + ' (' + signingFlowDesc + ')';
    } else {
      this.signerDescriptionFieldTarget.innerHTML = this.internalSignerTextValue + ' (' + signingFlowDesc + ')';
    }
    this.userImageFieldTarget.src = data.photo;
    if (this.hasUserPhoneInputTarget) this.userPhoneInputTarget.querySelector('input').value = data.phone;
    if (this.hasUserCvrInputTarget) this.userCvrInputTarget.querySelector('input').value = data.cvr;
  }

  swapSelectToForm() {
    this.signerSelectContainerTarget.classList.add(this.hiddenClass);
    this.signerTypeSelectContainerTarget.classList.add(this.hiddenClass);
    this.externalFullNameInputTarget.classList.add(this.hiddenClass);
    this.externalEmailInputTarget.classList.add(this.hiddenClass);
    this.recruiterWorkflowInputTarget.classList.add(this.hiddenClass);
    this.buttonsGroupTarget.classList.add(this.hiddenClass);
    this.element.classList.remove('editable');
    this.userDetailsTarget.classList.remove(this.hiddenClass);
    this.userDetailIconsTarget.classList.remove(this.hiddenClass);
    this.userImageTarget.classList.remove(this.hiddenClass);
    this.dragIconTarget.classList.remove(this.hiddenClass);
    this.orderDivTarget.classList.remove(this.hiddenClass);
    if (this.hasUserPhoneInputTarget) {
      this.userPhoneInputTarget.classList.add(this.hiddenClass);
    }
    if (this.hasUserCvrInputTarget) {
      this.userCvrInputTarget.classList.add(this.hiddenClass);
    }
    this.recountOrder();
  }

  changeWorkflowSelect() {
    const signerType = this.signerTypeSelectTarget.value;
    const selectedWorkflowId = parseInt(this.recruiterWorkflowSelectTarget.value);
    this.workflow = this.workflowDataValue.find((workflowData) => workflowData.id === selectedWorkflowId);
    // Sync to hidden field for form submission
    if (this.hasRecruiterWorkflowHiddenTarget) {
      this.recruiterWorkflowHiddenTarget.value = selectedWorkflowId;
    }
    this._updateSetButton();

    if (signerType === 'internal') {
      if (this.workflow && this.workflow.phone_presence && this.selectedSigner && (!this.selectedSigner.phone || this.selectedSigner.phone === '')) {
        this.userPhoneInputTarget.classList.remove(this.hiddenClass);
        this.userPhoneInputTarget.style.paddingRight = '33px';
        this.userPhoneInputTarget.style.paddingLeft = '0';
      } else {
        this.userPhoneInputTarget.classList.add(this.hiddenClass);
      }

      return;
    }

    if (this.workflow && this.workflow.cvr_presence) {
      this.userCvrInputTarget.classList.remove(this.hiddenClass);
      this.userCvrInputTarget.style.paddingLeft = '0';
      this.userCvrInputTarget.style.paddingRight = '33px';
    } else {
      this.userCvrInputTarget.classList.add(this.hiddenClass);
    }

    if (this.workflow && this.workflow.phone_presence) {
      this.userPhoneInputTarget.classList.remove(this.hiddenClass);
    } else {
      this.userPhoneInputTarget.classList.add(this.hiddenClass);
    }

    if (this.workflow && this.workflow.phone_presence && this.workflow && this.workflow.cvr_presence) {
      this.userCvrInputTarget.style.paddingLeft = '33px';
      this.userCvrInputTarget.style.paddingRight = '0';
    }
  }

  cancel(event) {
    event.preventDefault();
    if (this.userNameFieldTarget.innerHTML.trim() !== '') {
      this.swapSelectToForm();
    } else{
      this.containerTarget.remove();
    }
  }

  setSigner(event) {
    event.preventDefault();
    const signerType = this.signerTypeSelectTarget.value;
    if (signerType === 'external') {
      this.showExternalSigner();
    } else if (signerType === 'internal') {
      const userId = this.signerSelectTarget.value;
      this.setUserId(userId);
      this.updateFullDataAndShowSigner(userId);
    }
  }

  showEditForm() {
    this.signerTypeSelectContainerTarget.classList.remove(this.hiddenClass);
    this.buttonsGroupTarget.classList.remove(this.hiddenClass);
    this.element.classList.add('editable');
    this.userDetailsTarget.classList.add(this.hiddenClass);
    this.userDetailIconsTarget.classList.add(this.hiddenClass);
    this.userImageTarget.classList.add(this.hiddenClass);
    this.dragIconTarget.classList.add(this.hiddenClass);
    this.orderDivTarget.classList.add(this.hiddenClass);

    const signerType = this.signerTypeSelectTarget.value;
    if (signerType === 'external') {
      this.showExternalInputs();
    } else if (signerType === 'internal') {
      this.signerSelectContainerTarget.classList.remove(this.hiddenClass);
      this.recruiterWorkflowInputTarget.classList.remove(this.hiddenClass);
      this.recruiterWorkflowInputTarget.style.paddingLeft = '0';
      this.recruiterWorkflowInputTarget.style.paddingRight = '33px';
      if (this.workflow && this.workflow.phone_presence && this.selectedSigner && (!this.selectedSigner.phone || this.selectedSigner.phone === '')) {
        this.userPhoneInputTarget.classList.remove(this.hiddenClass);
        this.userPhoneInputTarget.style.paddingRight = '33px';
        this.userPhoneInputTarget.style.paddingLeft = '0';
      }
    }
  }

  externalSignerInput() {
    this._updateSetButton();
  }

  showExternalSigner() {
    this.userNameFieldTarget.innerHTML = this.externalFullNameInputTarget.querySelector('input').value.trim();
    this.userEmailFieldTarget.innerHTML = this.externalEmailInputTarget.querySelector('input').value.trim();
    this.userImageFieldTarget.src = this.defaultPhotoPathValue;
    this.signerDescriptionFieldTarget.innerHTML = this.externalSignerTextValue + ' (' + this._signingFlowDescription() + ')';
    this.swapSelectToForm();
  }

  checkboxSameSignerClicked(e) {
    if (e.target.checked) {
      $('button[name="add_signer_button"]').hide();
      $('.additional-signer').hide();
    } else {
      $('button[name="add_signer_button"]').show();
      $('.additional-signer').show();
    }
  }

  checkboxSameContractClicked(e) {
    if (e.target.checked) {
      localStorage.setItem('singleContract', true);
      window.frEditors.forEach(function(editor) {
        editor.html.set('')
      });
      $('.card-body:not(:first)').hide();
    } else {
      localStorage.setItem('singleContract', false);
      window.frEditors.forEach(function(editor) {
        editor.html.set('')
      });
      $('.card-body:not(:first)').show();
    }
  }

  recountOrder() {
    let count = 0;
    const containerDivsSet = new Set();

    document.querySelectorAll('.contract-signers').forEach(signersDiv => {
      signersDiv.querySelectorAll('div.signer-container').forEach(container => containerDivsSet.add(container));
    });

    const containerDivs = Array.from(containerDivsSet);

    containerDivs.forEach((container) => {
      const destroyInput = container.querySelector('input[data-nested-form-target="destroyInput"]');

      if (!destroyInput || destroyInput.value !== "1") {
        container.querySelector('.contract-order-input').value = ++count;
        container.querySelector('.contract-order').innerHTML = count;
      }
    });
  }

  removeSortableTarget() {
    this.containerTarget.removeAttribute('data-sortable-target');
  }

  _isElvium() {
    const form = this.element.closest('form');
    return form && form.classList.contains('elvium-signing-active');
  }

  _updateSetButton() {
    const signerType = this.signerTypeSelectTarget.value;
    const userId = this.signerSelectTarget.value;
    const hasSigner = userId && userId.trim() !== '';
    const hasWorkflow = this._isElvium() || (this.recruiterWorkflowSelectTarget.value && this.recruiterWorkflowSelectTarget.value.trim() !== '');

    if (signerType === 'external') {
      const fullName = this.externalFullNameInputTarget.querySelector('input').value;
      const email = this.externalEmailInputTarget.querySelector('input').value;
      if (fullName && fullName.trim() !== '' && email && email.trim() !== '' && hasWorkflow) {
        this.setButtonTarget.removeAttribute('disabled');
      } else {
        this.setButtonTarget.setAttribute('disabled', 'disabled');
      }
    } else {
      if (hasSigner && hasWorkflow) {
        this.setButtonTarget.removeAttribute('disabled');
      } else {
        this.setButtonTarget.setAttribute('disabled', 'disabled');
      }
    }
  }

  _signingFlowDescription() {
    if (this._isElvium()) {
      return 'Elvium Sign-off method';
    }
    // Visma: show provider name + workflow name
    const selectedWorkflowId = parseInt(this.recruiterWorkflowSelectTarget.value);
    this.workflow = this.workflowDataValue.find((workflowData) => workflowData.id === selectedWorkflowId)
      || this.workflowDataValue[0];
    return this.workflow ? 'Visma Addo, ' + this.workflow.name : 'Visma Addo';
  }
}
